import { RoleEnum, User } from '../../models';
import appEnv from '../configuration/appEnv';
import EventProvider from '../dataProviders/resources/event';
import { getUuid } from '../helpers/auth.utils';

import { clearKeepAliveTimer, keepAliveHandler } from './handlers';
import { subscribeToEvents } from './subscriber';

export const ALLOWED_LINE_IDS = new Set([
	'1',
	'2',
	'3',
	'4',
	'5',
	'6',
	'7',
	'8',
	'9',
	'10',
	'11',
	'12',
	'13',
	'14',
	'A',
	'B',
]);

const UUID_REGEX = /^[0-9a-zA-Z_-]{1,64}$/;

export function sanitizeLineId(lineId: unknown): string | undefined {
	if (typeof lineId !== 'string') return undefined;
	const trimmed = lineId.trim().toUpperCase();
	return ALLOWED_LINE_IDS.has(trimmed) ? trimmed : undefined;
}

export function sanitizeUuid(uuid: unknown): string | undefined {
	if (typeof uuid !== 'string') return undefined;
	const trimmed = uuid.trim();
	return UUID_REGEX.test(trimmed) ? trimmed : undefined;
}

let sseSource: EventSource | undefined;
let currentLineId: string | undefined;

export function getSseSource(): EventSource | undefined {
	return sseSource;
}

export function getCurrentLineId(): string | undefined {
	return currentLineId;
}

export function closeEventSource() {
	clearKeepAliveTimer();
	if (sseSource) {
		sseSource.close();
		sseSource = undefined;
	}
	currentLineId = undefined;
}

export function sanitizeBackendUrl(rawUrl: unknown): string {
	if (typeof rawUrl !== 'string' || !rawUrl.trim()) return '';
	try {
		const baseOrigin =
			typeof window !== 'undefined' && window.location?.origin
				? window.location.origin
				: 'http://localhost';
		const parsed = new URL(rawUrl.trim(), baseOrigin);
		if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
			return '';
		}
		return parsed.href.replace(/\/+$/, '');
	} catch {
		return '';
	}
}

export function buildEventSourceUrl(validLineId: string, validUuid?: string): string | undefined {
	const safeBase = sanitizeBackendUrl(appEnv.backendUrl);
	const fallbackOrigin =
		typeof window !== 'undefined' && window.location?.origin
			? window.location.origin
			: 'http://localhost';
	const base = safeBase || fallbackOrigin;

	try {
		const targetUrl = new URL(`${base}/v1/events/${validLineId}`);
		if (targetUrl.protocol !== 'http:' && targetUrl.protocol !== 'https:') {
			return undefined;
		}
		if (validUuid) {
			targetUrl.searchParams.set('uuid', validUuid);
		}
		return targetUrl.toString();
	} catch {
		return undefined;
	}
}

export function createEventSource(lineId = '') {
	const validLineId = sanitizeLineId(lineId);
	if (!validLineId) return;

	const validUuid = sanitizeUuid(getUuid());
	const sseUrl = buildEventSourceUrl(validLineId, validUuid);
	if (!sseUrl) return;

	closeEventSource();
	currentLineId = validLineId;
	keepAliveHandler();
	sseSource = new EventSource(sseUrl, {
		withCredentials: true,
	});
	return sseSource;
}

export function initEventSource(user: User, lineId = '', allowAdmin = false) {
	const validLineId = sanitizeLineId(lineId);
	if (!user || !validLineId) return;

	const { PROPRETE, TR, TRADMIN, TRMAL, AGENTM, ADMIN, PROPRETESNCF } = RoleEnum;
	const allowedUsers = allowAdmin
		? [PROPRETE, TR, TRADMIN, TRMAL, AGENTM, ADMIN, PROPRETESNCF]
		: [PROPRETE, TR, TRADMIN, TRMAL, AGENTM, PROPRETESNCF];

	if (!allowedUsers.includes(user.roles[0].id)) return;

	if (
		sseSource &&
		sseSource.readyState !== EventSource.CLOSED &&
		currentLineId === validLineId
	) {
		return;
	}

	createEventSource(validLineId);
	subscribeToEvents([
		'keepAlive',
		'usedStatusUpdate',
		'serviceUpdate',
		'serviceDelete',
		'incidentDelete',
		'incidentResolve',
		'npReminder',
		'degrafReminder',
		'tagReminder',
		'windshieldReminder',
		'stainReminder',
		'unitUpdate',
	]);
}

export function loadAlerts() {
	setTimeout(() => {
		EventProvider.loadNotifications().catch((error) => {
			console.error('Error:', error);
		});
	}, 2000);
}
